import { useState } from 'react'
import { Clock, Layers, Loader2, Trash2 } from 'lucide-react'
import { INDEX_SPECS, TIER1_INDICES } from '@apna/shared'
import { cn } from '@/lib/utils'
import { Notice } from '@/components/Notice'
import { InstrumentPicker } from '@/screens/terminal/InstrumentPicker'
import type { BasketDto } from '@/types/basket'

/**
 * Which underlying, or which draft — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE UNDERLYING IS ASKED FIRST BECAUSE IT CANNOT BE CHANGED LATER
 *
 * Every leg of a basket is a contract of one underlying — `openTrade` refuses
 * legs that span two, because a payoff curve plots value against one price.
 * So it is not a setting inside the builder that could be switched halfway;
 * it is the question the builder opens with, and the answer fixes the ladder.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DRAFTS COME FIRST WHEN THERE ARE ANY
 *
 * "Open yesterday's condor" and "start a new one" are the same decision made
 * two ways, and a trader who has built baskets before is almost always doing
 * the first. So the saved list leads when it has rows in it and the new-basket
 * card leads when it does not — the common case is never behind the rare one,
 * whichever one that is today.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A TILE IS A CHOICE; A SEARCH BOX IS A QUESTION
 *
 * The three indices are tiles with their lot size on them, because they are
 * what almost every basket trades and a tile is one click with nothing to
 * type. Everything else is behind a search field that LOOKS like a search
 * field — the first cut hid the picker behind the words "or any F&O name",
 * which read as a label rather than a control.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** "27 Oct", or "just now" for something touched in the last hour. */
function touched(iso: string): string {
  const at = new Date(iso).getTime()
  if (!Number.isFinite(at)) return ''
  const mins = Math.round((Date.now() - at) / 60_000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins} min ago`
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`
  return new Date(at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

export interface BasketStartProps {
  drafts: readonly BasketDto[]
  loading: boolean
  error: string | null
  busy: boolean
  onStart: (underlying: string) => void
  onOpen: (basket: BasketDto) => void
  onDelete: (basket: BasketDto) => void
  /** Most recently opened underlyings, for the picker's shortcut row. */
  recent: readonly string[]
}

export function BasketStart({
  drafts,
  loading,
  error,
  busy,
  onStart,
  onOpen,
  onDelete,
  recent,
}: BasketStartProps) {
  /*
   * Confirmed in place rather than through a dialog. A draft is cheap to
   * rebuild and a modal over a modal is the worst thing on this screen; the
   * row simply asks once and the answer is two clicks away from an accident.
   */
  const [confirming, setConfirming] = useState<string | null>(null)
  const hasDrafts = drafts.length > 0

  const newBasket = (
    <section className="bb-card" data-testid="basket-new">
      <h3 className="bb-card-h">Start a new basket</h3>
      <p className="bb-card-p">
        Every leg trades one underlying, so this is chosen once and fixes the ladder.
      </p>

      <div className="bb-tiles">
        {TIER1_INDICES.map((u) => (
          <button
            key={u}
            type="button"
            className="bb-tile"
            disabled={busy}
            data-testid={`basket-start-${u}`}
            onClick={() => onStart(u)}
          >
            <span className="bb-tile-n">{u}</span>
            <span className="bb-tile-s num">
              {INDEX_SPECS[u] ? `${INDEX_SPECS[u].lot} per lot` : 'index'}
            </span>
          </button>
        ))}
      </div>

      {/*
        THE PICKER IS THE SEARCH BOX. It draws its own magnifier, its own
        placeholder and its own Ctrl-K hint, so wrapping it in a row that
        drew a second magnifier and a second placeholder put two search
        fields side by side with only one of them live. Nothing around it.
      */}
      <div className="bb-any">
        <InstrumentPicker value="" onChange={onStart} recent={recent} />
      </div>
    </section>
  )

  const saved = (
    <section className="bb-card" data-testid="basket-saved">
      <h3 className="bb-card-h">
        <Layers size={14} className="text-muted-foreground" />
        Saved baskets
        {loading && <Loader2 size={12} className="animate-spin text-muted-foreground" />}
        {hasDrafts && <span className="bb-card-n num">{drafts.length}</span>}
      </h3>

      {error && (
        <Notice tone="error" className="mt-3">
          {error}
        </Notice>
      )}

      {!loading && !hasDrafts && !error && (
        <p className="bb-card-p">
          Nothing saved yet. A basket is kept whether you place it or close it
          half-built, so you can come back to it.
        </p>
      )}

      <ul className="bb-drafts">
        {drafts.map((b) => (
          <li key={b.id} className="bb-draft" data-testid={`basket-draft-${b.id}`}>
            <button type="button" className="bb-draft-open" onClick={() => onOpen(b)}>
              <span className="bb-draft-u num">{b.underlying}</span>
              <span className="min-w-0 flex-1">
                <span className="bb-draft-name">{b.name}</span>
                <span className="bb-draft-sub num">
                  {b.legs.length} leg{b.legs.length === 1 ? '' : 's'}
                  {b.placeCount > 0 && (
                    <span className="bb-draft-placed">placed {b.placeCount}×</span>
                  )}
                </span>
              </span>
              <span className="bb-draft-when num">
                <Clock size={11} />
                {touched(b.updatedAt)}
              </span>
            </button>

            {confirming === b.id ? (
              <span className="bb-draft-sure">
                <button
                  type="button"
                  className="yes"
                  data-testid={`basket-delete-yes-${b.id}`}
                  onClick={() => {
                    setConfirming(null)
                    onDelete(b)
                  }}
                >
                  Delete
                </button>
                <button type="button" onClick={() => setConfirming(null)}>
                  Keep
                </button>
              </span>
            ) : (
              <button
                type="button"
                className={cn('bb-draft-x')}
                aria-label={`Delete ${b.name}`}
                data-testid={`basket-delete-${b.id}`}
                onClick={() => setConfirming(b.id)}
              >
                <Trash2 size={13} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )

  return (
    <div className="bb-start" data-testid="basket-start">
      {hasDrafts ? (
        <>
          {saved}
          {newBasket}
        </>
      ) : (
        <>
          {newBasket}
          {saved}
        </>
      )}
    </div>
  )
}
